// ========== CONSTANTS ==========

export const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

const CACHE_KEY_BY_TICKER: Record<string, string> = {
  POL: 'pol_usd_price_cache',
  SOL: 'sol_usd_price_cache',
};

/**
 * Legacy export — preserved so existing tests/imports referencing the POL
 * cache key keep working during the multi-chain migration.
 */
export const CACHE_KEY = CACHE_KEY_BY_TICKER.POL;

// ========== TYPES ==========

export interface PriceCacheEntry {
  price: number;
  timestamp: number;
}

const resolveKey = (ticker: string): string => {
  const upper = ticker.toUpperCase();
  return CACHE_KEY_BY_TICKER[upper] ?? `${upper.toLowerCase()}_usd_price_cache`;
};

// ========== FUNCTIONS ==========

/**
 * Saves the current TOKEN→USD price to localStorage with timestamp.
 * `ticker` defaults to POL for backward compatibility with existing callers.
 */
export const savePriceToCache = (price: number, ticker: string = 'POL'): void => {
  try {
    const entry: PriceCacheEntry = {
      price,
      timestamp: Date.now(),
    };
    localStorage.setItem(resolveKey(ticker), JSON.stringify(entry));
  } catch {
    // Silently fail if localStorage is unavailable
  }
};

/**
 * Retrieves the cached price entry from localStorage.
 * Returns null if cache is empty or corrupted.
 */
export const getPriceFromCache = (ticker: string = 'POL'): PriceCacheEntry | null => {
  try {
    const raw = localStorage.getItem(resolveKey(ticker));
    if (!raw) return null;
    return JSON.parse(raw) as PriceCacheEntry;
  } catch {
    return null;
  }
};

/**
 * Checks if a cache entry is still valid (< 1 hour old).
 */
export const isCacheValid = (entry: PriceCacheEntry | null): boolean => {
  if (!entry) return false;
  if (typeof entry.timestamp !== 'number') return false;
  return Date.now() - entry.timestamp < CACHE_TTL_MS;
};

/**
 * Clears the price cache from localStorage.
 */
export const clearPriceCache = (ticker: string = 'POL'): void => {
  try {
    localStorage.removeItem(resolveKey(ticker));
  } catch {
    // Silently fail if localStorage is unavailable
  }
};
